import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { snakeCase } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  of,
  scan,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

import { ApiParams, CoreApiService, queryAll } from '../../../core-api.service';
import { TasksService } from '../../../deprecated-tasks/tasks.service';
import { ConfirmDialog } from '../../../dialogs';
import { FwLegacyDialogService as DialogService } from '../../../dialogs/dialog.service';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import { ProfileService } from '../../../profile/profile.service';
import { ProjectsService, UserOption } from '../../../projects/projects.service';
import { PermissionAction } from '../../../shared/enums/permission-action.enum';
import { DryrunTaskDeletionPreview } from '../../../shared/models/deprecated-task.model';
import { Paginated } from '../../../shared/models/paginated.model';
import { ReadTaskProtocol, ReadTaskProtocolInput } from '../../../shared/models/read-task-protocol.model';
import { User } from '../../../shared/models/user.model';
import { replayValue } from '../../../shared/observable.utils';
import { caseInsensitiveRegExp } from '../../../shared/string.utils';
import { DeleteTaskPreviewDialogComponent } from '../delete-task-preview/delete-task-preview-dialog.component';
import { ViewerProtocolEditorDialogComponent } from './viewer-protocol-editor/viewer-protocol-editor-dialog.component';

interface ProtocolRow extends ReadTaskProtocol {
  creator: User;
  lastModifiedDisplay: string;
}

@Component({
  templateUrl: './viewer-protocols-list.component.html',
  styleUrls: ['./viewer-protocols-list.component.scss'],
  selector: 'viewer-protocols-list',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ViewerProtocolsListComponent implements OnDestroy, OnInit, OnChanges {
  private dialog = inject(DialogService);
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private projectsService = inject(ProjectsService);
  private profileService = inject(ProfileService);
  private tasksService = inject(TasksService);

  @Input() projectId: string;
  @ViewChild('fwLoadTrigger') loadTrigger: ElementRef;

  columns: string[] = ['label', 'description', 'creator', 'modified', 'actions'];
  canModify = true;
  hasLoadingError = false;
  hasProtocols = false;
  isLoading = false;
  protocols$: Observable<ProtocolRow[]>;
  unfilteredProtocolsExist = false;
  userOptions: UserOption[] = [];
  readonly loadMore$ = new Subject<void>();
  readonly protocolSort$ = new BehaviorSubject<Sort>({ active: 'modified', direction: 'desc' });
  readonly filters = new FormGroup({
    creator: new FormControl(''),
    label: new FormControl(''),
  });
  readonly removeProtocol$ = new BehaviorSubject<ProtocolRow[]>([]);
  private nextAfterId: string = null;
  private subscriptions = {
    initData: Subscription.EMPTY,
    refresh: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
    dialogClosed: Subscription.EMPTY,
  };
  private dialogResult: MatDialogRef<ViewerProtocolEditorDialogComponent, ReadTaskProtocol>;

  get hasFilters(): boolean {
    return Object.values(this.filters.getRawValue()).some((value) => Boolean(value));
  }

  ngOnInit(): void {
    this.subscriptions.initData = queryAll(this.coreApi.users.query).subscribe((users) => {
      this.userOptions = users.map((user) => {
        return {
          ...user,
          label: `${user.firstname} ${user.lastname}`,
          value: user._id,
        };
      });
      this.loadData();
    });
  }

  ngOnChanges(): void {
    this.subscriptions.permissions.unsubscribe();
    this.subscriptions.permissions = combineLatest([
      this.projectsService.getProjectPermissions(this.projectId),
    ]).subscribe(([permissions]) => {
      this.canModify = permissions.includes(PermissionAction.FormAdmin);
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
    if (this.dialogResult) {
      this.dialogResult.close();
    }
  }

  get creatorFilter(): string {
    return this.filters.get('creator').value;
  }
  set creatorFilter(value: string | string[]) {
    this.filters.get('creator').setValue(value.toString());
  }

  get labelFilter(): string {
    return this.filters.get('label').value;
  }
  set labelFilter(value: string) {
    this.filters.get('label').setValue(value);
  }

  loadData(): void {
    this.isLoading = true;
    this.protocols$ = combineLatest([
      this.profileService.exhaustiveMode$,
      replayValue(this.filters).pipe(debounceTime(200)),
      this.protocolSort$.pipe(map((sort) => ({ ...sort, active: snakeCase(sort.active) }))),
    ]).pipe(
      switchMap(([exhaustive, filterValues, sortValue]) => {
        const queryParams: ApiParams = {
          exhaustive,
          limit: 10,
          filter: this.getFilterString(filterValues),
          sort: `${sortValue.active}:${sortValue.direction}`,
        };
        this.nextAfterId = null;
        return this.loadMore$.pipe(
          filter(() => Boolean(this.nextAfterId)),
          startWith(undefined as void),
          switchMap(() => {
            this.isLoading = true;
            return this.coreApi.readTaskProtocols.query({
              ...queryParams,
              ...(this.nextAfterId ? { after_id: this.nextAfterId } : {}),
            });
          }),
          scan((allProtocols, newPage) => {
            const combined = allProtocols.concat(newPage.results);
            if (!this.hasFilters) {
              this.unfilteredProtocolsExist = combined.length > 0;
            }
            this.nextAfterId =
              combined.length < newPage.total && newPage.results.length > 0
                ? newPage.results[newPage.results.length - 1]._id
                : null;
            return combined;
          }, []),
          map((protocols) => {
            this.hasProtocols = protocols.length > 0;
            this.isLoading = false;
            return protocols.map((protocols) => this.getProtocolRow(protocols));
          }),
          switchMap((protocols) => {
            // to update the data in the table w/o rerunning the query
            return this.removeProtocol$.pipe(
              switchMap((tasksToRemove) => {
                for (const taskToRemove of tasksToRemove) {
                  const index = protocols.indexOf(taskToRemove);
                  if (index >= 0) {
                    protocols.splice(index, 1);
                  }
                }
                return of(protocols);
              }),
              tap((protocols) => {
                let nextAfterExists = false;
                for (const protocol of Object.values(protocols)) {
                  if (protocol._id === this.nextAfterId) {
                    nextAfterExists = true;
                  }
                }
                // if the protocol with nextAfterId was deleted, use the last protocol on the page
                if (nextAfterExists === false && this.nextAfterId !== null) {
                  this.nextAfterId = protocols.length > 0 ? protocols.slice(-1)[0]._id : null;
                }
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        this.hasLoadingError = true;
        this.isLoading = false;
        this.logger.error(error);
        this.notifications.error('tasks.get.error');
        return of([] as ProtocolRow[]);
      }),
    );
  }

  loadMore(): void {
    // ensures continuous loading in the case the loadTrigger element is still visible after initial fetch for more data
    const triggerTop = this.loadTrigger.nativeElement.getBoundingClientRect().top;
    const triggerParentBottom = this.loadTrigger.nativeElement.parentElement.getBoundingClientRect().bottom;
    const triggerIsVisible = triggerTop < triggerParentBottom;
    if (!this.isLoading && this.nextAfterId && triggerIsVisible) {
      this.loadMore$.next();
    }
  }

  getProtocolRow(protocol: ReadTaskProtocol): ProtocolRow {
    const modifiedDate = parseISO(protocol.modified);
    return {
      ...protocol,
      creator: this.userOptions.find((user) => user._id === protocol.origin.id),
      lastModifiedDisplay: `${formatDistanceToNowStrict(modifiedDate)} ago`,
    };
  }

  getFilterString(filterValues: { [key: string]: string }): string {
    const queryFilter = [];
    queryFilter.push(`parents.project=${this.projectId}`);
    if (filterValues.label) {
      queryFilter.push(`label=~${caseInsensitiveRegExp(filterValues.label)}`);
    }
    if (filterValues.creator) {
      queryFilter.push(`origin.id=${filterValues.creator}`);
    }
    return queryFilter.join(',');
  }

  deleteProtocol(protocol: ProtocolRow): void {
    // before deletion, query all tasks that use this protocol
    this.coreApi.readTaskProtocols
      .deleteDryrun(protocol._id)
      .pipe(
        switchMap((tasks: Paginated<DryrunTaskDeletionPreview[]>) => {
          if (tasks.total > 0) {
            // if tasks are found, show a table view of tasks which will be deleted
            return this.dialog
              .open(DeleteTaskPreviewDialogComponent, {
                data: { protocolId: protocol._id, protocolLabel: protocol.label },
              })
              .afterClosed()
              .pipe(filter((confirm) => Boolean(confirm)));
          } else {
            // if no tasks use this protocol, show a simple confirmation
            return this.dialog
              .open(ConfirmDialog, {
                width: '440px',
                data: {
                  title: 'Delete Viewer Protocol',
                  html:
                    `Are you sure you want to delete <b>${protocol.label}</b>?<br><br>Any Completed tasks will still ` +
                    'be shown in the Tasks List and will not be affected.',
                },
              })
              .afterClosed()
              .pipe(filter((confirm) => Boolean(confirm)));
          }
        }),
        switchMap(() => {
          return this.coreApi.readTaskProtocols.delete(protocol._id);
        }),
      )
      .subscribe({
        next: () => {
          this.removeProtocol$.next([protocol]);
          this.notifications.success('viewerProtocols.delete.success');
        },
        error: (error) => {
          this.logger.error(error);
          this.notifications.error('viewerProtocols.delete.error');
        },
      });
  }

  duplicateProtocol(protocol: ReadTaskProtocolInput): void {
    combineLatest([this.coreApi.forms.get(protocol.form_id), this.coreApi.viewerConfigs.get(protocol.viewer_config_id)])
      .pipe(
        switchMap(([form, viewerConfig]) => {
          return combineLatest([this.coreApi.forms.post(form), this.coreApi.viewerConfigs.post(viewerConfig)]);
        }),
        switchMap(([newForm, newViewerConfig]) => {
          return this.coreApi.readTaskProtocols.post({
            ...protocol,
            label: `Copy of ${protocol.label}`,
            form_id: newForm._id,
            viewer_config_id: newViewerConfig._id,
          });
        }),
      )
      .subscribe({
        next: () => {
          this.notifications.success('viewerProtocols.duplicate.success');
          this.loadData();
        },
        error: (error) => {
          this.logger.error(error);
          this.notifications.error('viewerProtocols.duplicate.error');
        },
      });
  }

  editProtocol(protocol?: ReadTaskProtocol): void {
    this.dialogResult = this.dialog.open(ViewerProtocolEditorDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      position: { right: '0' },
      panelClass: 'border-radius-0',
      data: {
        protocol,
        projectId: this.projectId,
      },
    });
    this.subscriptions.dialogClosed = this.dialogResult.afterClosed().subscribe({
      next: (protocol) => {
        this.dialogResult = null;
        this.tasksService.getReadProtocols({ refresh: true }); // refresh the cache
        if (protocol) {
          this.loadData();
        }
      },
    });
  }
}
